<script setup lang="ts">
import type { Schemas } from "#shopware";
import defu from "defu";
import { useRoute, useRouter } from "vue-router";
import SwPagination from "@shopware-pwa/cms-base/components/SwPagination.vue";
import SwProductCard from "~/components/cms/SwProductCard.vue";

const defaultLimit = 24;
const defaultPage = 1;

type Translations = {
  listing: {
    noProducts: string;
    perPage: string;
    product: string;
    products: string;
  };
};
let translations: Translations = {
  listing: {
    noProducts: "No products found 😔",
    perPage: "Per Page:",
    product: "Product",
    products: "Products",
  },
};
translations = defu(translations, useCmsTranslations()) as Translations;

const {
  items,
  clearWishlist,
  getWishlistProducts,
  currentPage,
  totalPagesCount,
  canSyncWishlist,
} = useWishlist();
defineOptions({
  name: "WishlistPage",
});
const { apiClient } = useShopwareContext();
const products = ref<Schemas["Product"][]>([]);
const isLoading = ref(false);
const { t } = useI18n();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const route = useRoute();
const router = useRouter();
useBreadcrumbs([
  {
    name: t("breadcrumbs.wishlist"),
    path: "/wishlist",
  },
]);
const limit = ref(route.query.limit ? Number(route.query.limit) : defaultLimit);
const clearWishlistHandler = async () => {
  try {
    isLoading.value = true;
    await clearWishlist();
  } finally {
    isLoading.value = false;
  }
};
const loadProductsByItemIds = async (itemIds: string[]): Promise<void> => {
  isLoading.value = true;

  try {
    const { data } = await apiClient.invoke("readProduct post /product", {
      body: { ids: itemIds || items.value },
    });

    if (data?.elements) {
      products.value = data.elements;
    }
  } catch (error) {
    console.error("[wishlist][loadProductsByItemIds]", error);
  }

  isLoading.value = false;
};

const changeLimit = async (limit: Event) => {
  const select = limit.target as HTMLSelectElement;
  const query = {
    ...route.query,
    limit: Number(select.value),
    page: defaultPage,
  };

  await router.push({
    query: query,
  });
  await getWishlistProducts(query);
};

const changePage = async (page: number) => {
  await router.push({
    query: {
      page: page,
      limit: limit.value,
    },
  });
  await getWishlistProducts(route.query);
};

watch(
  items,
  (items, oldItems) => {
    if (items.length !== oldItems?.length) {
      products.value = products.value.filter(({ id }) => items.includes(id));
    }
    if (!items.length) {
      return;
    }
    loadProductsByItemIds(items);
  },
  {
    immediate: true,
  },
);

const initalRoute = defu(route);
watch(
  () => route,
  (newRoute) => {
    if (initalRoute.path !== newRoute.path) {
      getWishlistProducts(); // update wishlist items when you leave wishlist page
      return;
    }
    if (Object.keys(newRoute.query).length > 0) {
      return;
    }
    // this fires to reset the page when query are removed/empty on client side navigation for the same page (without hard reload)
    getWishlistProducts({
      limit: defaultLimit,
      page: defaultPage,
    });
    limit.value = defaultLimit;
  },
  { deep: true },
);

onMounted(() => {
  const route = useRoute();
  const limit = ref(
    route.query.limit ? Number(route.query.limit) : defaultLimit,
  );
  const page = ref(route.query.page ? Number(route.query.page) : defaultPage);
  // this request is needed to start with the first page and the current limit or default limit for the pagination on wishlist page
  getWishlistProducts({
    limit: limit.value,
    page: page.value,
  });
});
</script>

<template>
  <div class="wishlist-page" data-testid="wishlist-wrapper">
    <LayoutBreadcrumbs :breadcrumbs="breadcrumbs" class="mx-5" />

    <div class="wish-list mt-4 px-0 md:px-4">
      <div class="container mx-auto relative pb-12 sm:pb-24">
        <!-- Wishlist is completed -->

        <div
          v-if="isLoading"
          class="relative w-full inset-0 flex items-center h-[400px] justify-center z-10 bg-white/50"
        >
          <div
            class="!h-[60px] !w-[60px] i-carbon-progress-bar-round animate-spin c-secondary-500"
          />
        </div>

        <div v-else-if="products.length">
          <div
            class="cust-title-wishilist flex items-center justify-between mb-0 md:mb-4"
          >
            <div class="title">
              <h3
                class="text-2xl md:text-3xl lg:text-4xl font-bold leading-snug"
              >
                {{ $t("wishlist.header")
                }}<span class="ml-2">({{ products.length }})</span>
              </h3>
            </div>
            <div class="edit-wishlist hidden md:block">
              <button class="btn" @click="clearWishlistHandler">
                {{ $t("wishlist.clearWishlist") }}
              </button>
            </div>
          </div>

          <div class="isotope container p-0 pt-7 lg:pt-10">
            <div
              v-if="!isLoading"
              class="grid grid-cols-1 xs:grid-cols-2 laptop:grid-cols-3 gap-x-5 gap-y-10 text-center"
            >
              <SwProductCard
                v-for="product in products"
                :key="product.id"
                :product="product"
              />
            </div>

            <div v-if="canSyncWishlist && products.length > 0" class="mt-10">
              <div
                class="show-more text-center mb-5"
                :class="{ hidden: products.length == count }"
              >
                <button
                  id="show-more"
                  class="btn-outline uppercase"
                  @click="changePage(currentPage + 1)"
                  :disabled="loadmoreLoading"
                >
                  <!-- Show More Products -->
                  <span v-if="loadmoreLoading">{{
                    $t("product.load.isLoading")
                  }}</span>
                  <span v-else>{{ $t("product.load.more") }}</span>
                </button>
              </div>

              <div
                class="progress shown w-full md:w-[360px] mx-auto"
                :class="{ hidden: products.length == count }"
              >
                <div class="w-full bg-platinum h-[6px] rounded mb-5 relative">
                  <!-- Progress bar itself -->
                  <div
                    id="progress-bar"
                    class="bg-arsenic h-[4px] rounded absolute top-1/2 inset-y-1/2 mt-[-2px]"
                    :style="{ width: `${(products.length / count) * 100}%` }"
                  ></div>
                </div>

                <!-- Progress text -->
                <div
                  class="progress-shown flex items-center justify-center text-davy-grey space-x-2"
                >
                  <div id="progress-text">
                    {{ products.length }} {{ $t("product.load.of") }}
                    {{ count }}
                  </div>
                  <span>{{ $t("product.load.shown") }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="edit-wishlist md:hidden flex justify-center">
            <button class="btn" @click="clearWishlistHandler">
              {{ $t("wishlist.clearWishlist") }}
            </button>
          </div>
        </div>
        <!-- Empty of wishlist -->
        <div
          v-else
          class="flex flex-col items-center col mx-auto"
          data-testid="wishlist-empty"
        >
          <div class="!w-48 !h-48 i-carbon-favorite" />
          <h1 class="my-3 text-3xl font-extrabold">
            {{ $t("wishlist.emptyLabel") }}
          </h1>
          <p class="my-4">{{ $t("wishlist.emptyText") }}</p>
          <NuxtLink
            :to="formatLink(`/`)"
            class="btn"
            date-testid="wishlist-empty-continue-link"
          >
            {{ $t("wishlist.continueShopping") }}
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>
